<template>
  <card-dialog
    v-model="modalVisible"
    max-width="520px"
  >
    <template v-slot:title>
      <v-card-title class="grey-bg px-6 py-4">
        <div v-if="playerFormData.id">
          Edit player
        </div>
        <div v-else>
          Add new player
        </div>
      </v-card-title>
    </template>

    <v-card-text
      class="pa-6"
    >
      <validation-observer
        ref="validation"
        v-slot="{ handleSubmit }"
      >
        <form
          id="player-form"
          ref="form"
          @submit.prevent="handleSubmit(onSubmit)"
        >
          <v-stepper
            v-model="currentStep"
            class="elevation-0"
          >
            <!--<editor-fold desc="Stepper Header">-->
            <v-stepper-header class="elevation-0">
              <v-btn-toggle
                v-model="currentStep"
                mandatory
                dense
                class="v-btn-toggle--block rounded"
              >
                <v-btn
                  class="text-capitalize"
                  :value="1"
                  :color="isBasicDetailsStepCompleted && !playerFormData.id ? 'success' : ''"
                >
                  {{ $t('new_diagnostic_protocols.basic_details.title') }}
                </v-btn>
                <v-btn
                  class="text-capitalize"
                  :value="2"
                  :color="isPersonalDetailsStepCompleted && !playerFormData.id ? 'success' : ''"
                  :disabled="!isBasicDetailsStepCompleted"
                >
                  Personal Details
                </v-btn>
                <v-btn
                  class="text-capitalize"
                  :value="3"
                  :color="isSportsDetailsStepCompleted && !playerFormData.id ? 'success' : ''"
                  :disabled="!isPersonalDetailsStepCompleted"
                >
                  {{ $t('new_player.sports_details.title') }}
                </v-btn>
              </v-btn-toggle>
            </v-stepper-header>
            <!--</editor-fold>-->

            <!--<editor-fold desc="Stepper Items Content">-->
            <v-stepper-items>
              <!--<editor-fold desc="Personal Info Step Content">-->
              <v-stepper-content
                step="1"
                class="px-0 py-3"
              >
                <validation-observer
                  v-if="currentStep >= 1"
                  ref="basicDetailsStepValidation"
                >
                  <!--<editor-fold description="Name input">-->
                  <v-row
                    no-gutters
                  >
                    <v-col class="pr-1">
                      <validation-provider
                        v-slot="{ errors }"
                        rules="required"
                      >
                        <v-text-field
                          v-model="playerFormData.first_name"
                          :error-messages="errors"
                          :label="$t('text_input.data.first_name.title')"
                          name="name"
                          dense
                          outlined
                          append-icon="fa-light fa-address-card"
                        />
                      </validation-provider>
                    </v-col>
                    <v-col class="pl-1">
                      <validation-provider
                        v-slot="{ errors }"
                        rules="required"
                      >
                        <v-text-field
                          v-model="playerFormData.last_name"
                          :error-messages="errors"
                          :label="$t('text_input.data.last_name.title')"
                          name="last_name"
                          dense
                          outlined
                          append-icon="fa-light fa-address-card"
                        />
                      </validation-provider>
                    </v-col>
                  </v-row>
                  <!--</editor-fold>-->

                  <!--<editor-fold desc="username input">-->
                  <validation-provider
                    v-slot="{ errors }"
                  >
                    <v-text-field
                      v-model="playerFormData.username"
                      :error-messages="errors"
                      :label="$t('text_input.user.username.title')"
                      name="username"
                      :placeholder="$t('text_input.user.username.placeholder')"
                      append-icon="fa-light fa-user"
                      dense
                      outlined
                    />
                  </validation-provider>
                  <!--</editor-fold>-->

                  <!--<editor-fold desc="email input">-->
                  <validation-provider
                    v-slot="{ errors }"
                    rules="email"
                  >
                    <v-text-field
                      v-model="playerFormData.email"
                      :error-messages="errors"
                      :label="$t('text_input.user.email.title')"
                      name="email"
                      dense
                      outlined
                      :hint="$t('text_input.user.account.hint')"
                      append-icon="fa-light fa-envelope"
                    />
                  </validation-provider>
                  <!--</editor-fold>-->
                </validation-observer>
              </v-stepper-content>
              <!--</editor-fold>-->

              <!--<editor-fold desc="Additional Info Step Content">-->
              <v-stepper-content
                step="2"
                class="pa-0"
              >
                <validation-observer
                  v-if="currentStep >= 2"
                  ref="personalDetailsStepValidation"
                >
                  <!--<editor-fold desc="photo input">-->
                  <validation-provider
                    v-slot="{ errors }"
                  >
                    <dropzone-field
                      v-model="playerFormData.photo"
                      label-idle="<div class='font-weight-light'><div class='blue--text text--darken-4 font-weight-regular'>Upload player image</div> by dragging it here or <span class='filepond--label-action font-italic'>browsing your PC</span></div>"
                      input-name="photo"
                      dense
                      outlined
                      :errors="errors"
                      class="mb-6"
                    />
                    <div
                      v-if="errors.length > 0"
                      class="v-messages theme--light error--text"
                      role="alert"
                    >
                      <div class="v-messages__wrapper">
                        <div
                          v-for="(error, i) in errors"
                          :key="`photo-input-error-${i}`"
                          class="v-messages__message"
                        >
                          {{ error }}
                        </div>
                      </div>
                    </div>
                  </validation-provider>
                  <!--</editor-fold>-->

                  <!--<editor-fold desc="pin input">-->
                  <validation-provider
                    v-slot="{ errors }"
                  >
                    <v-text-field
                      v-model="playerFormData.pin"
                      :error-messages="errors"
                      :label="$t('text_input.data.oib.title')"
                      name="pin"
                      :placeholder="$t('text_input.data.oib.placeholder')"
                      append-icon="fa-light fa-passport"
                      dense
                      outlined
                    />
                  </validation-provider>
                  <!--</editor-fold>-->

                  <!--<editor-fold desc="address input">-->
                  <validation-provider
                    v-slot="{ errors }"
                  >
                    <v-text-field
                      v-model="playerFormData.address"
                      :error-messages="errors"
                      :label="$t('text_input.data.current_address.title')"
                      name="address"
                      :placeholder="$t('text_input.data.current_address.placeholder')"
                      append-icon="fa-light fa-location-dot"
                      dense
                      outlined
                    />
                  </validation-provider>
                  <!--</editor-fold>-->

                  <!--<editor-fold desc="phone input">-->
                  <validation-provider
                    v-slot="{ errors }"
                  >
                    <v-text-field
                      v-model="playerFormData.phone"
                      :error-messages="errors"
                      :label="$t('text_input.data.phone_number.title')"
                      name="phone"
                      :placeholder="$t('text_input.data.phone_number.placeholder')"
                      append-icon="fa-light fa-mobile-screen-button"
                      dense
                      outlined
                    />
                  </validation-provider>
                  <!--</editor-fold>-->

                  <!--<editor-fold desc="birth_date input">-->
                  <v-menu
                    ref="menu"
                    v-model="datePickerMenuVisible"
                    :close-on-content-click="false"
                    :return-value.sync="playerFormData.birth_date"
                    transition="scale-transition"
                    offset-x
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <validation-provider
                        v-slot="{ errors }"
                        rules="required"
                      >
                        <v-text-field
                          readonly
                          :value="playerFormData.birth_date ? $dayjs(playerFormData.birth_date).format(getDateTimeFormatForUser('D/M/YYYY')) : ''"
                          :error-messages="errors"
                          :label="$t('table_headers.admin_all_players.date_of_birth')"
                          name="birth_date"
                          suffix=""
                          dense
                          outlined
                          v-bind="attrs"
                          append-icon="fa-light fa-calendar-days"
                          v-on="on"
                        />
                      </validation-provider>
                    </template>
                    <v-date-picker
                      v-model="playerFormData.birth_date"
                      :first-day-of-week="1"
                      no-title
                      scrollable
                      :max="$dayjs().format()"
                      @input="onSelectDate"
                    />
                  </v-menu>
                  <!--</editor-fold>-->

                  <!--<editor-fold desc="birth_city input">-->
                  <validation-provider
                    v-slot="{ errors }"
                  >
                    <v-text-field
                      v-model="playerFormData.birth_city"
                      :error-messages="errors"
                      name="birth_city"
                      :label="$t('text_input.data.birth_city.title')"
                      dense
                      outlined
                      :placeholder="$t('text_input.data.birth_city.placeholder')"
                      append-icon="fa-light fa-location-arrow"
                    />
                  </validation-provider>
                  <!--</editor-fold>-->

                  <!--<editor-fold desc="parents input">-->
                  <validation-provider
                    v-slot="{ errors }"
                  >
                    <v-text-field
                      v-model="playerFormData.parents"
                      :error-messages="errors"
                      :label="$t('parent_name.label')"
                      name="parents"
                      dense
                      outlined
                      :placeholder="$t('players_parent_name.placeholder')"
                      append-icon="fa-light fa-family"
                    />
                  </validation-provider>
                  <!--</editor-fold>-->

                  <!--<editor-fold desc="school input">-->
                  <validation-provider
                    v-slot="{ errors }"
                  >
                    <v-text-field
                      v-model="playerFormData.school"
                      :error-messages="errors"
                      :label="$t('text_input.data.school_name.title')"
                      name="school"
                      dense
                      outlined
                      :placeholder="$t('text_input.data.school_name.placeholder')"
                      append-icon="fa-light fa-graduation-cap"
                    />
                  </validation-provider>
                  <!--</editor-fold>-->
                </validation-observer>
              </v-stepper-content>
              <!--</editor-fold>-->

              <!--<editor-fold desc="Account Info Step Content">-->
              <v-stepper-content
                step="3"
                class="pa-0"
              >
                <ValidationObserver
                  v-if="isSportsDetailsStep"
                  ref="sportsDetailsStepValidation"
                >
                  <!--<editor-fold desc="team_id input">-->
                  <validation-provider
                    v-slot="{ errors }"
                  >
                    <v-select
                      v-model="playerFormData.team_id"
                      name="team_id"
                      :items="teamSelectItems"
                      item-value="id"
                      item-text="name"
                      :error="errors.length > 0"
                      :label="$t('add_new.team')"
                      dense
                      outlined
                      :placeholder="$t('dropdown.position.placeholder')"
                    />
                  </validation-provider>
                  <!--</editor-fold>-->

                  <!--<editor-fold desc="sport_details input">-->
                  <validation-provider
                    v-slot="{ errors }"
                  >
                    <v-text-field
                      v-model="playerFormData.sport_details"
                      :error-messages="errors"
                      :label="$t('sport_details.label')"
                      name="sport_details"
                      :placeholder="$t('enter_players_details.placeholder')"
                      append-icon="fa-light fa-info"
                      dense
                      outlined
                    />
                  </validation-provider>
                  <!--</editor-fold>-->

                  <!--<editor-fold desc="registration_number input">-->
                  <validation-provider
                    v-slot="{ errors }"
                  >
                    <v-text-field
                      v-model="playerFormData.registration_number"
                      :error-messages="errors"
                      :label="$t('text_input.fifa_id')"
                      name="registration_number"
                      :placeholder="$t('text_input.fifa_id.placeholder')"
                      :prefix="$t('text_input.fifa_id.title')"
                      append-icon="fa-light fa-id-card-clip"
                      dense
                      outlined
                    />
                  </validation-provider>
                  <!--</editor-fold>-->

                  <!--<editor-fold desc="player_position_id input">-->
                  <validation-provider
                    v-slot="{ errors }"
                  >
                    <v-select
                      v-model="playerFormData.player_position_id"
                      :items="playerPositions.data"
                      :loading="playerPositions.loading"
                      :error="errors.length > 0"
                      item-value="id"
                      item-text="name"
                      name="player_position_id"
                      outlined
                      :label="$t('dropdown.position.title')"
                      :placeholder="$t('dropdown.position.placeholder')"
                      dense
                    >
                      <template
                        v-slot:item="{ item }"
                      >
                        <team-color-dot :color="item.color" />{{ item.name }} ({{ item.short_name }})
                      </template>
                      <template
                        v-slot:selection="{ item }"
                      >
                        <team-color-dot :color="item.color" />{{ item.name }} ({{ item.short_name }})
                      </template>
                    </v-select>
                  </validation-provider>
                  <!--</editor-fold>-->

                  <!--<editor-fold desc="player_status_id">-->
                  <validation-provider
                    v-slot="{ errors }"
                  >
                    <v-select
                      v-model="playerFormData.player_status_id"
                      :items="playerStatuses.data"
                      :loading="playerStatuses.loading"
                      :error="errors.length > 0"
                      item-value="id"
                      name="player_status_id"
                      :label="$t('dropdown.status.title')"
                      outlined
                      :placeholder="$t('dropdown.position.placeholder')"
                      dense
                    >
                      <template v-slot:item="{ item }">
                        <span class="text-capitalize">{{ item.name }}</span>
                      </template>
                      <template v-slot:selection="{ item }">
                        <span class="text-capitalize">{{ item.name }}</span>
                      </template>
                    </v-select>
                  </validation-provider>
                  <!--</editor-fold>-->
                </ValidationObserver>
              </v-stepper-content>
              <!--</editor-fold>-->
            </v-stepper-items>
            <!--</editor-fold>-->
          </v-stepper>
        </form>
      </validation-observer>
    </v-card-text>
    <template v-slot:actions>
      <v-card-actions class="pa-4">
        <div class="flex-grow-1">
          <v-btn
            key="action-button-3"
            type="submit"
            color="primary"
            block
            large
            :loading="submitting"
            @click="onSubmit()"
          >
            {{ isSportsDetailsStep || playerFormData.id ? 'Save Player' : 'Continue' }}
          </v-btn>
          <v-btn
            v-if="playerFormData.id"
            class="mt-2"
            color="error"
            text
            block
            @click="deleteConfirmationDialogVisible = true"
          >
            {{ $t('button.delete') }}
          </v-btn>
        </div>
      </v-card-actions>
    </template>

    <delete-confirmation-dialog
      :visible="deleteConfirmationDialogVisible"
      @confirmed="deletePlayer()"
    />
  </card-dialog>
</template>

<script>
import CardDialog from '@/components/helpers/CardDialog'
import TeamColorDot from '@/components/inputs/TeamColorDot.vue'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import { PlayerApiService, PlayerPositionApiService, PlayerStatusApiService } from '@/data/services'
import { EventBus } from '@/plugins/bus'
import DropzoneField from '@/components/inputs/DropzoneField'
import { cloneDeep } from 'lodash'
import DeleteConfirmationDialog from '@/components/dialogs/DeleteConfirmationDialog'
import { toCamelCase, getDateTimeFormatForUser } from '@/utils/helpers'

const emptyPlayerFormData = {
  id: null,
  first_name: null,
  last_name: null,
  birth_date: null,
  pin: null,
  birth_city: null,
  address: null,
  phone: null,
  parents: null,
  school: null,
  team_id: null,
  sport_details: null,
  registration_number: null,
  player_position_id: null,
  player_status_id: null,
  username: null,
  email: null,
  photo: null
}

export default {
  name: 'PlayerFormDialog',
  components: { DeleteConfirmationDialog, DropzoneField, CardDialog, TeamColorDot },
  mixins: [deleteConfirmationMixin],
  props: {
    player: {
      type: Object,
      default: () => ({})
    },
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      playerFormData: emptyPlayerFormData,
      playerStatuses: {
        data: [],
        loading: false,
        loaded: false
      },
      playerPositions: {
        data: [],
        loading: false,
        loaded: false
      },
      currentStep: 1,
      datePickerMenuVisible: false,
      submitting: false
    }
  },
  computed: {
    modalVisible: {
      get: function () {
        return this.visible
      },
      set: function (value) {
        this.$emit('update:visible', value)
      }
    },
    isBasicDetailsStep() {
      return this.currentStep === 1
    },
    isPersonalDetailsStep() {
      return this.currentStep === 2
    },
    isSportsDetailsStep() {
      return this.currentStep === 3
    },
    isBasicDetailsStepCompleted() {
      return this.currentStep > 1 || this.playerFormData.id
    },
    isPersonalDetailsStepCompleted() {
      return this.currentStep > 2 || this.playerFormData.id
    },
    isSportsDetailsStepCompleted() {
      return this.currentStep > 3 || this.playerFormData.id
    },
    teamSelectItems() {
      return this.$store.getters[this.$storeGetters.APP_TEAMS]
    }
  },
  watch: {
    visible(newValue) {
      if (!newValue || !this.player) return

      this.playerFormData = { ...this.playerFormData, ...cloneDeep(this.player) }
    }
  },
  created() {
    EventBus.$on(this.$events.OPEN_DIALOG_PLAYER_FORM, () => {
      this.playerFormData = emptyPlayerFormData
      this.modalVisible = true
    })

    EventBus.$on(this.$events.EDIT_PLAYER, (player) => {
      this.playerFormData = { ...this.playerFormData, ...cloneDeep(player) }
      this.modalVisible = true
    })

    this.fetchPlayerPositions()
    this.fetchPlayerStatuses()
  },
  methods: {
    getDateTimeFormatForUser,
    async onSubmit() {
      if (!(await this.$refs.validation.validate())) return

      if (this.currentStep === 3 || this.playerFormData.id) {
        await this.save()

        return
      }

      this.nextStep()
    },
    async save() {
      try {
        this.submitting = true

        let response

        if (this.playerFormData.id) {
          response = await PlayerApiService.update(this.playerFormData)
        } else {
          response = await PlayerApiService.create(this.playerFormData)
        }

        if (this.playerFormData.id) {
          this.$store.dispatch(this.$storeActions.ALERT, {
            text: this.$t('alert.player_updated.success'),
            color: 'success'
          })

          EventBus.$emit(this.$events.PLAYER_UPDATED, response.data)
        } else {
          this.$store.dispatch(this.$storeActions.ALERT, {
            text: this.$t('alert.adding_player.success'),
            color: 'success'
          })

          EventBus.$emit(this.$events.PLAYER_CREATED, response.data)
        }

        this.modalVisible = false
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.adding_player.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.submitting = false
      }
    },
    async fetchPlayerStatuses() {
      if (this.playerStatuses.loaded) return

      try {
        this.playerStatuses.loading = true

        const response = await PlayerStatusApiService.getAll()

        this.playerStatuses.data = response.data.map((status) => {
          return {
            id: status.id,
            name: this.$t(`player_status.${status.name}`)
          }
        })
        this.playerStatuses.loaded = true
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_data.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.playerStatuses.loading = false
      }
    },
    async fetchPlayerPositions() {
      if (this.playerPositions.loaded) return

      try {
        this.playerPositions.loading = true

        const response = await PlayerPositionApiService.getAll()

        this.playerPositions.data = response.data.map((position) => {
          return {
            id: position.id,
            name: this.$t(`player_position.${toCamelCase(position.name)}`),
            short: this.$t(`player_position.${toCamelCase(position.name)}.short`)
          }
        })
        this.playerPositions.loaded = true
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_data.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.playerPositions.loading = false
      }
    },
    async deletePlayer() {
      try {
        this.deleting = true

        await PlayerApiService.delete(this.playerFormData.id)

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.player_delete.success'),
          color: 'success'
        })

        this.modalVisible = false

        EventBus.$emit(this.$events.PLAYER_DELETED, this.playerFormData)
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.player_delete.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.deleting = false
      }
    },
    nextStep() {
      if (this.currentStep >= 3) return

      this.currentStep++
    },
    onSelectDate(val) {
      this.$refs.menu.save(val)
    }
  }
}
</script>

<style lang="scss">
#player-form {
  .v-btn-toggle {
    .v-btn {
      &.v-btn--disabled {
        color: inherit !important;
        background-color: inherit !important;
      }
      &.success {
        color: #FFFFFF !important;
      }
    }
  }
}
</style>
